import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, FileText, Receipt, Pencil, Loader2, TrendingUp, TrendingDown, Printer, Save, XCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US");
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

// "حركات الدين" — chronological per-customer ledger showing every invoice,
// receipt, and manual adjustment with a running balance. Used when a
// customer disputes their debt — admin can show this page as proof.
export default function DebtLedgerModal({ customerId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  // Inline-edit state for receipts (سندات القبض). Admin can adjust the
  // amount of any receipt and the customer's debt is recomputed by the
  // existing PATCH /admin/receipts/{id} endpoint.
  const [editingReceiptId, setEditingReceiptId] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const reload = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/customers/${customerId}/debt-ledger`);
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API}/admin/customers/${customerId}/debt-ledger`);
        if (alive) setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "فشل تحميل سجل الدين");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [customerId]);

  const startEdit = (e) => {
    setEditingReceiptId(e.ref_id || e.ref);
    setEditAmount(String(Math.abs(e.delta || 0)));
  };

  const cancelEdit = () => {
    setEditingReceiptId(null);
    setEditAmount("");
  };

  const saveEdit = async () => {
    const amt = Number(editAmount);
    if (!editingReceiptId || amt <= 0) {
      toast.error("المبلغ يجب أن يكون أكبر من صفر");
      return;
    }
    setSavingEdit(true);
    try {
      await axios.patch(`${API}/admin/receipts/${editingReceiptId}`, { amount: amt });
      toast.success("تم تعديل السند وإعادة حساب الدين");
      cancelEdit();
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التعديل");
    } finally {
      setSavingEdit(false);
    }
  };

  const printLedger = () => {
    const css = `
      body{font-family:'Tahoma','Arial',sans-serif;direction:rtl;padding:24px;color:#1E3A5F}
      h1{font-size:20px;margin:0 0 4px;color:#1E3A5F}
      .meta{color:#5C6E64;font-size:12px;margin-bottom:16px}
      table{width:100%;border-collapse:collapse;margin-top:12px}
      th{background:#FEF3C7;color:#92400E;font-size:11px;padding:6px;text-align:right;border:1px solid #E4EBE6}
      td{padding:6px;font-size:11px;border:1px solid #E4EBE6;text-align:right}
      .plus{color:#15803D;font-weight:bold}
      .minus{color:#DC2626;font-weight:bold}
      .bal{font-weight:bold;background:#F9FAFB}
      .sum{background:#FFFBEB;padding:12px;border-radius:8px;margin-top:16px;font-size:13px}
    `;
    const head = `<html lang="ar" dir="rtl"><head><meta charset="utf-8"><title>سجل حركات الدين</title><style>${css}</style></head><body>`;
    const c = data.customer;
    const events = data.events || [];
    const rows = events.map((e) => `
      <tr>
        <td>${fmtDate(e.date)}</td>
        <td>${e.description}</td>
        <td>${e.ref || "—"}</td>
        <td class="${e.delta > 0 ? "plus" : "minus"}" dir="ltr">${e.delta > 0 ? "+" : ""}${fmt(e.delta)}</td>
        <td class="bal" dir="ltr">${fmt(e.balance_after)}</td>
      </tr>
    `).join("");
    const html = `${head}
      <h1>سجل حركات الدين — ${c.full_name || c.phone}</h1>
      <div class="meta">${c.market_name ? `الماركت: ${c.market_name} · ` : ""}الهاتف: ${c.phone}</div>
      <table>
        <thead>
          <tr><th>التاريخ</th><th>الوصف</th><th>المرجع</th><th>التغيير</th><th>الرصيد</th></tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="sum">
        <strong>عدد الحركات:</strong> ${data.summary.events_count} ·
        <strong>إجمالي الفواتير:</strong> ${fmt(data.summary.total_charged)} د.ع ·
        <strong>إجمالي القبض:</strong> ${fmt(data.summary.total_received)} د.ع ·
        <strong>الدين الحالي:</strong> <span dir="ltr">${fmt(data.summary.net)} د.ع</span>
      </div>
    </body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  // ─── WhatsApp share — text-only summary of every event + grand total ───
  const shareWhatsApp = () => {
    if (!data) return;
    const lines = [];
    lines.push(`📋 كشف حساب — جملتي`);
    lines.push(`الزبون: ${data.customer?.full_name || ""}`);
    if (data.customer?.market_name) lines.push(`الماركت: ${data.customer.market_name}`);
    if (data.customer?.phone) lines.push(`الهاتف: ${data.customer.phone}`);
    lines.push(`التاريخ: ${new Date().toLocaleDateString("en-GB")}`);
    lines.push("─".repeat(22));
    (data.events || []).forEach((e, i) => {
      const sign = e.delta > 0 ? "+" : "";
      lines.push(`${i + 1}. ${fmtDate(e.date)} — ${e.description}\n    ${sign}${fmt(e.delta)} (الرصيد: ${fmt(e.balance_after)})`);
    });
    lines.push("─".repeat(22));
    lines.push(`إجمالي الفواتير: ${fmt(data.summary?.total_charged)} د.ع`);
    lines.push(`إجمالي القبض: ${fmt(data.summary?.total_received)} د.ع`);
    lines.push(`الدين الحالي: ${fmt(data.summary?.net)} د.ع`);
    lines.push(`https://jumlaty.online`);
    window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        dir="rtl"
        data-testid="debt-ledger-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#2c5282] text-white px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            <h2 className="font-black text-base">حركات الدين الكاملة</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full" data-testid="debt-ledger-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12 text-[#5C6E64]">
            <Loader2 className="w-6 h-6 animate-spin ml-2" />
            جاري التحميل...
          </div>
        ) : !data ? (
          <div className="flex-1 flex items-center justify-center py-12 text-[#5C6E64]">لا توجد بيانات</div>
        ) : (
          <>
            {/* Customer + Totals */}
            <div className="border-b border-[#E4EBE6] p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 bg-[#F9FAFB]">
              <div className="col-span-2">
                <p className="text-[10px] text-[#5C6E64]">الزبون</p>
                <p className="font-black text-[#1E3A5F]">{data.customer.full_name || "—"}</p>
                {data.customer.market_name && (
                  <p className="text-xs text-[#5C6E64]">{data.customer.market_name}</p>
                )}
                <p className="text-[10px] text-[#5C6E64]" dir="ltr">{data.customer.phone}</p>
              </div>
              <div>
                <p className="text-[10px] text-[#5C6E64]">إجمالي الفواتير</p>
                <p className="font-black text-[#DC2626]" dir="ltr">{fmt(data.summary.total_charged)} د.ع</p>
              </div>
              <div>
                <p className="text-[10px] text-[#5C6E64]">إجمالي القبض</p>
                <p className="font-black text-[#15803D]" dir="ltr">{fmt(data.summary.total_received)} د.ع</p>
              </div>
              <div className="col-span-2 sm:col-span-4 bg-[#FEF3C7] rounded-lg p-3 mt-2 flex items-center justify-between">
                <span className="font-black text-[#92400E] text-sm">الدين الحالي (محسوب)</span>
                <span className="font-black text-[#92400E] text-lg" dir="ltr" data-testid="ledger-current-debt">
                  {fmt(data.summary.net)} د.ع
                </span>
              </div>
              {Math.abs((data.customer.current_debt_stored || 0) - (data.summary.net || 0)) >= 1 && (
                <div className="col-span-2 sm:col-span-4 bg-[#FEE2E2] border border-[#DC2626] rounded-lg p-2 text-[11px] text-[#7F1D1D]">
                  ⚠ الدين المخزّن ({fmt(data.customer.current_debt_stored)} د.ع) لا يطابق الحساب الصحيح. اضغط "إعادة حساب" بصفحة تدقيق الديون.
                </div>
              )}
            </div>

            {/* Timeline */}
            <div className="flex-1 overflow-y-auto p-3">
              {(data.events || []).length === 0 ? (
                <div className="text-center py-10 text-[#5C6E64]">لا توجد حركات بعد</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-[#FEF3C7] text-[#92400E] sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-right font-bold">التاريخ</th>
                      <th className="px-2 py-2 text-right font-bold">الوصف</th>
                      <th className="px-2 py-2 text-center font-bold">المرجع</th>
                      <th className="px-2 py-2 text-center font-bold">التغيير</th>
                      <th className="px-2 py-2 text-center font-bold">الرصيد</th>
                      <th className="px-2 py-2 text-center font-bold w-16">إجراء</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.events.map((e, i) => {
                      const receiptId = e.ref_id || (e.type === "receipt" ? e.ref : null);
                      const isEditing = editingReceiptId && receiptId === editingReceiptId;
                      return (
                        <tr key={i} className="border-t border-[#E4EBE6] hover:bg-[#FFFBEB]" data-testid={`ledger-row-${i}`}>
                          <td className="px-2 py-2 text-right whitespace-nowrap" dir="ltr">{fmtDate(e.date)}</td>
                          <td className="px-2 py-2 text-right">
                            <div className="flex items-center gap-1.5">
                              {e.type === "order" && <FileText className="w-3.5 h-3.5 text-[#DC2626]" />}
                              {e.type === "receipt" && <Receipt className="w-3.5 h-3.5 text-[#15803D]" />}
                              {e.type === "manual" && <Pencil className="w-3.5 h-3.5 text-[#92400E]" />}
                              <span>{e.description}</span>
                            </div>
                          </td>
                          <td className="px-2 py-2 text-center text-[#5C6E64]">{e.ref || "—"}</td>
                          <td className="px-2 py-2 text-center font-black" dir="ltr" style={{ color: e.delta > 0 ? "#DC2626" : "#15803D" }}>
                            {isEditing ? (
                              <input
                                type="number"
                                value={editAmount}
                                onChange={(ev) => setEditAmount(ev.target.value)}
                                data-testid={`ledger-edit-amount-${i}`}
                                className="w-24 px-1.5 py-1 rounded border-2 border-[#15803D] text-center text-sm font-bold"
                                autoFocus
                              />
                            ) : (
                              e.delta > 0 ? (
                                <span className="inline-flex items-center gap-0.5"><TrendingUp className="w-3 h-3" />+{fmt(e.delta)}</span>
                              ) : (
                                <span className="inline-flex items-center gap-0.5"><TrendingDown className="w-3 h-3" />{fmt(e.delta)}</span>
                              )
                            )}
                          </td>
                          <td className="px-2 py-2 text-center font-black text-[#1E3A5F]" dir="ltr">{fmt(e.balance_after)}</td>
                          <td className="px-2 py-2 text-center">
                            {/* Edit ability is only for receipts (سندات القبض).
                                Invoices/orders are edited from the orders tab. */}
                            {e.type === "receipt" && receiptId && (
                              isEditing ? (
                                <div className="flex items-center gap-1 justify-center">
                                  <button
                                    onClick={saveEdit}
                                    disabled={savingEdit}
                                    data-testid={`ledger-save-${i}`}
                                    className="bg-[#15803D] hover:bg-[#126b32] disabled:opacity-50 text-white rounded p-1"
                                    title="حفظ"
                                  >
                                    {savingEdit ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    data-testid={`ledger-cancel-${i}`}
                                    className="bg-[#E4EBE6] hover:bg-[#d4dad6] text-[#5C6E64] rounded p-1"
                                    title="إلغاء"
                                  >
                                    <XCircle className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => startEdit(e)}
                                  data-testid={`ledger-edit-${i}`}
                                  className="bg-[#FEF3C7] hover:bg-[#FDE68A] text-[#92400E] rounded p-1"
                                  title="تعديل قيمة السند"
                                >
                                  <Pencil className="w-3 h-3" />
                                </button>
                              )
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-[#E4EBE6] p-3 flex items-center justify-end gap-2 bg-[#F9FAFB]">
              <button
                onClick={shareWhatsApp}
                disabled={!data.events?.length}
                className="flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1ebe57] disabled:opacity-50 text-white font-bold text-xs px-3 py-1.5 rounded-full"
                data-testid="debt-ledger-whatsapp"
              >
                💚 واتساب
              </button>
              <button
                onClick={printLedger}
                disabled={!data.events?.length}
                className="flex items-center gap-1.5 bg-[#1E3A5F] hover:bg-[#152a47] disabled:opacity-50 text-white font-bold text-xs px-3 py-1.5 rounded-full"
                data-testid="debt-ledger-print"
              >
                <Printer className="w-3.5 h-3.5" />
                طباعة كشف
              </button>
              <button
                onClick={onClose}
                className="bg-[#5C6E64] hover:bg-[#465048] text-white font-bold text-xs px-3 py-1.5 rounded-full"
              >
                إغلاق
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
